/**
 * Telefone/celular/WhatsApp — libphonenumber-js (default BR).
 */
import {
  parsePhoneNumberFromString,
  AsYouType,
  isValidPhoneNumber as libIsValidPhoneNumber,
} from "libphonenumber-js/max";

const SENTINEL_RE = /^(grupo:|.*@g\.us$)/i;

/** Overflow INT32 (2^31-1) quando `clientes.whatsapp` era INT e recebia celular BR. */
const INT32_OVERFLOW_PHONE_DIGITS = new Set(["2147483647", "552147483647"]);

export function isPhoneSentinel(v) {
  return SENTINEL_RE.test(String(v ?? "").trim());
}

export function phoneDigitsOnly(v) {
  return String(v ?? "").replace(/\D+/g, "");
}

/** Valor corrompido por overflow INT em coluna legado `whatsapp` → (21) 4748-3647. */
export function isInt32OverflowPhone(v) {
  return INT32_OVERFLOW_PHONE_DIGITS.has(phoneDigitsOnly(v));
}

/** Telefone utilizável para exibição (ignora flags legadas 0/1, INT_MAX e dígitos curtos). */
export function isUsableStoredPhone(v) {
  const s = String(v ?? "").trim();
  if (!s) return false;
  if (isPhoneSentinel(s)) return false;
  if (isInt32OverflowPhone(s)) return false;
  return phoneDigitsOnly(s).length >= 8;
}

export function sanitizeStoredPhoneForDisplay(v) {
  if (!isUsableStoredPhone(v)) return "";
  return String(v).trim();
}

const CLIENT_CONTACT_PHONE_FIELDS = [
  "numero_whatsapp",
  "whatsapp",
  "celular",
  "telefone",
  "phone",
  "mobile",
  "telephone",
];

/** Primeiro telefone utilizável entre os campos do cliente/conversa. */
export function pickClientDisplayPhone(source) {
  if (!source || typeof source !== "object") return "";
  for (const k of CLIENT_CONTACT_PHONE_FIELDS) {
    const v = sanitizeStoredPhoneForDisplay(source[k]);
    if (v) return v;
  }
  return "";
}

/** Celular/WhatsApp do cadastro — não usa telefone fixo. */
const CLIENT_WHATSAPP_PHONE_FIELDS = [
  "numero_whatsapp",
  "whatsapp",
  "celular",
  "mobile",
  "phone",
];

export function pickClientWhatsappPhone(source) {
  if (!source || typeof source !== "object") return "";
  for (const k of CLIENT_WHATSAPP_PHONE_FIELDS) {
    const v = sanitizeStoredPhoneForDisplay(source[k]);
    if (v) return v;
  }
  return "";
}

export function clientHasWhatsappPhone(source) {
  return Boolean(pickClientWhatsappPhone(source));
}

export function clientWhatsappPhoneDigits(source, defaultCountry = "BR") {
  const raw = pickClientWhatsappPhone(source);
  if (!raw) return "";
  return toE164Digits(raw, defaultCountry) || phoneDigitsOnly(raw);
}

/** Telefone formatado para exibição em cards e listas. */
export function formatClientDisplayPhone(source, defaultCountry = "BR") {
  const raw = pickClientDisplayPhone(source);
  return raw ? formatPhoneDisplay(raw, defaultCountry) : "";
}

/**
 * Valor exibido no input a partir do que veio da API (E.164, máscara BR, etc.).
 */
export function formatStoredPhoneForInput(raw, defaultCountry = "BR") {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (isPhoneSentinel(s)) return s;
  if (!isUsableStoredPhone(s)) return "";

  let pn = parsePhoneNumberFromString(s, defaultCountry);
  if (!pn || !pn.isValid()) {
    const d = phoneDigitsOnly(s);
    if (d) pn = parsePhoneNumberFromString(`+${d}`, defaultCountry);
  }
  if (!pn || !pn.isValid()) pn = parsePhoneNumberFromString(s);
  if (pn && pn.isValid()) {
    return pn.country === defaultCountry ? pn.formatNational() : pn.formatInternational();
  }
  return s;
}

/**
 * Remove zero(s) de tronco BR após DDI 55 (ou prefixo 0/00 nacional).
 * Ex.: 55001143951891 → 551143951891. Evita discagem Twilio com destino inválido (31005).
 */
function coerceBrazilTrunkPrefix(digitsOrRaw) {
  let d = phoneDigitsOnly(digitsOrRaw);
  if (!d) return "";
  if (d.startsWith("00")) d = d.replace(/^00+/, "");
  if (d.startsWith("55") && d.length >= 12) {
    const nsn = d.slice(2).replace(/^0+/, "");
    if (nsn.length === 10 || nsn.length === 11) {
      const fixed = parsePhoneNumberFromString(`+55${nsn}`, "BR");
      if (fixed && fixed.isValid()) return fixed.number.replace(/^\+/, "");
    }
  }
  if (d.startsWith("0")) {
    const nsn = d.replace(/^0+/, "");
    if (nsn.length === 10 || nsn.length === 11) {
      const fixed = parsePhoneNumberFromString(nsn, "BR");
      if (fixed && fixed.isValid()) return fixed.number.replace(/^\+/, "");
    }
  }
  return "";
}

function dialPhoneInvalidError(originalDigits) {
  const d = phoneDigitsOnly(originalDigits);
  if (/^55(0+)/.test(d) || /^0{1,2}[1-9]\d{7,11}$/.test(d)) {
    return "Número inválido: remova o zero de tronco após o código do país (ex.: +55 11 4395-1891, não +55 011… nem +55 00 11…).";
  }
  return "Número inválido para discagem. Confira DDD e DDI (ex.: +55 11 98765-4321).";
}

/**
 * E.164 sem '+' para enviar à API; sentinelas intactas.
 */
export function toE164Digits(input, defaultCountry = "BR") {
  if (input == null || input === "") return "";
  const raw = String(input).trim();
  if (isPhoneSentinel(raw)) return raw;

  const digits = phoneDigitsOnly(raw);
  let pn = parsePhoneNumberFromString(raw, defaultCountry);
  // Só aceita parse BR quando válido: 598… (UY) vira +55 598 inválido se ignorar isValid().
  if (!pn || !pn.isValid()) {
    if (digits) pn = parsePhoneNumberFromString(`+${digits}`, defaultCountry);
  }
  if (!pn || !pn.isValid()) pn = parsePhoneNumberFromString(raw);
  if (!pn || !pn.isValid()) pn = digits ? parsePhoneNumberFromString(`+${digits}`) : null;
  if (pn && pn.isValid()) return pn.number.replace(/^\+/, "");

  if (!digits) return "";

  const trunkFixed = coerceBrazilTrunkPrefix(digits);
  if (trunkFixed) return trunkFixed;

  if (
    defaultCountry === "BR" &&
    !digits.startsWith("55") &&
    (digits.length === 10 || digits.length === 11)
  ) {
    const asBr = parsePhoneNumberFromString(digits, "BR");
    if (asBr && asBr.isValid() && asBr.country === "BR") {
      return asBr.number.replace(/^\+/, "");
    }
  }
  return digits;
}

/**
 * E.164 com '+' para discagem (Twilio Voice / softphone).
 * Corrige caracteres inválidos, DDI ausente e zero de tronco BR.
 * Identidades `client:` são preservadas.
 *
 * @returns {{ ok: boolean, e164: string, digits: string, display: string, error: string, corrected: boolean }}
 */
export function normalizeDialPhone(input, defaultCountry = "BR") {
  const raw = String(input ?? "").trim();
  if (!raw) {
    return {
      ok: false,
      e164: "",
      digits: "",
      display: "",
      error: "Informe o número de destino.",
      corrected: false,
    };
  }
  if (raw.toLowerCase().startsWith("client:")) {
    return {
      ok: true,
      e164: raw,
      digits: raw,
      display: raw,
      error: "",
      corrected: false,
    };
  }

  const originalDigits = phoneDigitsOnly(raw);
  const digits = toE164Digits(raw, defaultCountry);
  if (!digits || isPhoneSentinel(digits)) {
    return {
      ok: false,
      e164: "",
      digits: "",
      display: raw,
      error: dialPhoneInvalidError(originalDigits),
      corrected: false,
    };
  }

  // Mínimo razoável: DDI(2) + DDD(2) + número(8) = 12 (BR); outros países ≥ 10 dígitos totais.
  if (digits.length < 10) {
    return {
      ok: false,
      e164: "",
      digits: "",
      display: raw,
      error: "Número incompleto. Inclua DDD (Brasil) ou + e o código do país.",
      corrected: false,
    };
  }

  const e164 = digits.startsWith("+") ? digits : `+${digits}`;
  const d = phoneDigitsOnly(digits);
  const dialable =
    libIsValidPhoneNumber(e164) ||
    (defaultCountry ? libIsValidPhoneNumber(e164, defaultCountry) : false) ||
    // Fallback BR: libphonenumber pode marcar inválido DDD que colide com DDI.
    (d.startsWith("55") &&
      (d.length === 12 || d.length === 13) &&
      /^55[1-9]\d{9,10}$/.test(d));
  if (!dialable) {
    return {
      ok: false,
      e164: "",
      digits: "",
      display: raw,
      error: dialPhoneInvalidError(originalDigits),
      corrected: false,
    };
  }

  const display = formatPhoneDisplay(e164, defaultCountry) || e164;
  const corrected =
    phoneDigitsOnly(raw) !== digits ||
    String(raw).replace(/\s+/g, "") !== e164;

  return {
    ok: true,
    e164,
    digits,
    display,
    error: "",
    corrected: Boolean(corrected || originalDigits !== digits),
  };
}

export function formatPhoneDisplay(input, defaultCountry = "BR") {
  if (input == null || input === "") return "";
  const raw = String(input).trim();
  if (isPhoneSentinel(raw)) return raw;
  if (!isUsableStoredPhone(raw)) return "";

  let pn = parsePhoneNumberFromString(raw, defaultCountry);
  if (!pn || !pn.isValid()) {
    const d = phoneDigitsOnly(raw);
    if (d) pn = parsePhoneNumberFromString(`+${d}`, defaultCountry);
  }
  if (!pn || !pn.isValid()) return raw;
  return pn.country === defaultCountry ? pn.formatNational() : pn.formatInternational();
}

export function isValidPhoneField(value, defaultCountry = "BR") {
  if (value == null || String(value).trim() === "") return true;
  if (isPhoneSentinel(String(value))) return true;
  return libIsValidPhoneNumber(String(value), defaultCountry) || libIsValidPhoneNumber(`+${phoneDigitsOnly(value)}`, defaultCountry);
}

export function getPhoneValidationMessage(value, defaultCountry = "BR") {
  if (value == null || String(value).trim() === "") return "";
  if (isPhoneSentinel(String(value))) return "";
  if (isValidPhoneField(value, defaultCountry)) return "";
  return "Número de telefone inválido. Inclua DDD (Brasil) ou + e código do país.";
}

export function phonesEqual(a, b, defaultCountry = "BR") {
  const da = toE164Digits(a, defaultCountry);
  const db = toE164Digits(b, defaultCountry);
  if (!da || !db) return false;
  if (isPhoneSentinel(da) || isPhoneSentinel(db)) return String(a).trim() === String(b).trim();
  return da === db;
}

/**
 * Chave de dedupe BR: DDD + 8 dígitos locais (ignora o 9º de celular).
 * Assim "(48) 8835-3608" e "(48) 98835-3608" colapsam no mesmo destino.
 */
export function brPhoneMatchKey(digitsOrRaw) {
  let d = phoneDigitsOnly(digitsOrRaw);
  if (!d) return "";
  if (d.startsWith("55")) d = d.slice(2);
  if (d.length === 11 && d[2] === "9") return `${d.slice(0, 2)}${d.slice(3)}`;
  if (d.length === 10) return d;
  return d;
}

/**
 * Destinos para o discador: celular/WhatsApp primeiro; dedupe preferindo o número com 9º dígito.
 * Retorna E.164 com '+' para valor estável no select.
 */
export function buildCustomerDialDestinations(source, extraPhones = [], defaultCountry = "BR") {
  const ordered = [
    source?.celular,
    source?.mobile,
    source?.whatsapp,
    source?.numero_whatsapp,
    source?.phone,
    source?.phoneNumber,
    source?.telefone,
    ...(Array.isArray(extraPhones) ? extraPhones : []),
  ];
  const byKey = new Map();
  ordered.forEach((raw, rank) => {
    if (!isUsableStoredPhone(raw)) return;
    const n = normalizeDialPhone(raw, defaultCountry);
    if (!n.ok) return;
    const key = brPhoneMatchKey(n.digits) || n.digits;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { e164: n.e164, display: n.display || n.e164, digits: n.digits, rank });
      return;
    }
    // Preferir versão mais longa (celular com 9) mantendo a ordem do primeiro campo.
    if (n.digits.length > prev.digits.length) {
      byKey.set(key, { e164: n.e164, display: n.display || n.e164, digits: n.digits, rank: prev.rank });
    }
  });
  return [...byKey.values()]
    .sort((a, b) => a.rank - b.rank)
    .map(({ e164, display }) => ({ e164, display }));
}

/** Match tolerante a cadastros legados (sem DDI ou 9). */
export function phonesLikelyMatch(peerDigitsOrRaw, clientRow, defaultCountry = "BR") {
  const peer = toE164Digits(peerDigitsOrRaw, defaultCountry);
  const a = phoneDigitsOnly(clientRow?.celular || clientRow?.telefone || clientRow?.phone || "");
  const w = phoneDigitsOnly(peer || peerDigitsOrRaw);
  if (!a || !w) return false;
  const clientRaw = clientRow?.celular || clientRow?.telefone || clientRow?.phone || "";
  if (phonesEqual(peerDigitsOrRaw, clientRaw, defaultCountry)) return true;
  if (a.length >= 8 && w.length >= 8 && (a.slice(-11) === w.slice(-11) || a.slice(-10) === w.slice(-10))) return true;
  return a.endsWith(w.slice(-10)) || w.endsWith(a.slice(-10));
}

/**
 * Formata texto digitado/colar (AsYouType novo a cada valor completo).
 */
export function formatPhoneAsYouType(input, defaultCountry = "BR") {
  const s = String(input ?? "");
  if (!s.trim()) return "";
  if (isPhoneSentinel(s)) return s;
  const useIntl = s.trim().startsWith("+");
  const ayt = new AsYouType(useIntl ? undefined : defaultCountry);
  return ayt.input(s);
}

export { AsYouType, libIsValidPhoneNumber as isValidPhoneNumber };
